import React, { FC } from "react";
import { Option } from "components/Signup/components/Customiser/Option";
import Switch from "ui-kit/Switch/Switch";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import Customiser from "../Customiser";
import { formatCurrency } from "utils/utils";
import {
  useAllValidAnimals,
  useCurrentAnimal,
} from "components/Signup/store/selectors/animals";
import { useLottieAnimation } from "hooks/useLottieAnimation";
import Stars from "../../../../assets/animations/stars.json";
import { useWebTranslation } from "translations";
import styles from "./ChoosePreventiveCarePackage.module.css";
import { BillingIntervalText } from "../BillingIntervalText";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";

const PackageDetails = ({
  total,
  reflectOnAllInsurances,
  onChangeReflectOnAllInsurances,
  id,
}: {
  total: number;
  reflectOnAllInsurances: boolean;
  onChangeReflectOnAllInsurances: VoidFunction;
  id: string;
}) => {
  const { t } = useWebTranslation();
  const animals = useAllValidAnimals();

  const ref = useLottieAnimation({ animation: Stars, loop: false });

  return (
    <div className={styles.packageDetailsContainer}>
      <Typography variant="body3" id={id}>
        <strong>
          {t("signup.preventive_step.package_details", {
            defaultValue: "Ajouter à tous les contrats de mes animaux",
          })}
        </strong>
      </Typography>
      <div className={styles.content}>
        <div className={styles.buttonContent}>
          <div style={{ width: 50 }}>
            <Switch
              active={reflectOnAllInsurances}
              activeColor="green200"
              onChange={onChangeReflectOnAllInsurances}
              aria-labelledby={id}
            />
          </div>
          <Typography variant="body3" color="grey500">
            {formatCurrency((total / 100) * animals.length)}
            <BillingIntervalText color="grey500" />
          </Typography>
        </div>
        {reflectOnAllInsurances ? (
          <div ref={ref} className={styles.sparkles} />
        ) : null}
      </div>
    </div>
  );
};

type ChoosePreventiveCareProps = {
  annualBilling: boolean;
  currentTotal: number;
  currentPreventiveCareAddOnLimit?: number;
  noSelectionOption?: { total: number; label: string };
  limits: { value: number; total: number }[];
  onChange: (carePackageAddOnLimit?: number) => void;
  lowestPrice: number;
  reflectOnAllInsurances: boolean;
  onChangeReflectOnAllInsurances: VoidFunction;
};

const ChoosePreventativeCarePackage: FC<ChoosePreventiveCareProps> = ({
  annualBilling,
  currentTotal,
  currentPreventiveCareAddOnLimit,
  limits,
  onChange,
  noSelectionOption,
  reflectOnAllInsurances,
  onChangeReflectOnAllInsurances,
}) => {
  const { t } = useWebTranslation();
  const animals = useAllValidAnimals();

  const animal = useCurrentAnimal();
  const insurance = useCurrentInsuranceByAnimalId(animal.id);

  if (!limits.length) {
    return null;
  }

  return (
    <>
      <Gutter size={2} />

      {noSelectionOption ? (
        <>
          <Option
            onChange={onChange}
            option={{
              title: t("global.nothanks"),
              value: undefined,
              totalDiff: noSelectionOption.total - currentTotal,
            }}
            isActive={currentPreventiveCareAddOnLimit === undefined}
            name="preventive-care-package"
          />
          <Gutter size={1} />
        </>
      ) : null}

      <Customiser
        name="preventive-care-package"
        options={[
          {
            value: 0,
            title: t("preventive_care_package.no_coverage"),
            subTitle: t("preventive_care_package.no_coverage_subtitle", {
              defaultValue: "Continuer sans budget préventif",
            }),
            totalDiff: 0,
            details:
              animals.length > 1 ? (
                <PackageDetails
                  id="none"
                  total={0}
                  reflectOnAllInsurances={reflectOnAllInsurances}
                  onChangeReflectOnAllInsurances={
                    onChangeReflectOnAllInsurances
                  }
                />
              ) : null,
          },
          ...limits.map(({ value, total }, index) => ({
            value,
            title: formatCurrency(value / 100),
            subTitle: t("preventive_care_package.coverage_subtitle", {
              defaultValue: "Couverture annuelle",
            }),
            totalDiff: total / 100,
            totalDiffTitle: annualBilling
              ? t("global.cost_per_year")
              : t("global.cost_per_month"),

            details:
              animals.length > 1 ? (
                <PackageDetails
                  id={`id-${index}`}
                  total={total}
                  reflectOnAllInsurances={reflectOnAllInsurances}
                  onChangeReflectOnAllInsurances={
                    onChangeReflectOnAllInsurances
                  }
                />
              ) : null,
            disabled: value !== 100_00 && insurance.limit === 1_000_00,
            disabledMessage: t(
              "preventive_care_package.coverage_not_available"
            ),
          })),
        ]}
        value={currentPreventiveCareAddOnLimit ?? 0}
        onChange={onChange}
        direction="column"
        noBadge
        showSigned={true}
      />
    </>
  );
};

export default ChoosePreventativeCarePackage;
